import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter 
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Calendar } from '../components/ui/calendar';
import { 
  Briefcase, 
  Plus, 
  Calendar as CalendarIcon, 
  FileText, 
  GraduationCap, 
  Clock,
  CheckCircle,
  AlertTriangle,
  User,
  Shield,
  Building,
  Printer,
  Download,
  Mail,
  MessageSquare,
  MoreHorizontal,
  Eye
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { format } from 'date-fns';
import DocumentActionRequestsPanel from '../components/DocumentActionRequestsPanel';

// ICRCL Details
const ICRCL_NAME = "Innovative Care and Rehab Centre Limited (ICRCL)";
const ICRCL_ADDRESS = "City Gate House, 246-250 Romford Road, London, E7 9HZ";
const ICRCL_PHONE = "+44 7380 350059";
const ICRCL_EMAIL = "info@icrcl.co.uk";

export default function HRPage() {
  const { api, user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [training, setTraining] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [trainingDialogOpen, setTrainingDialogOpen] = useState(false);
  const [policyDialogOpen, setPolicyDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewPolicyDialog, setViewPolicyDialog] = useState(null);
  const [editPolicyDialog, setEditPolicyDialog] = useState(null);
  
  // Super Admin check
  const isSuperAdmin = user?.email?.toLowerCase() === 'admin@icrcl.co.uk';
  
  // Policy permissions (includes delegated access)
  const [policyPermissions, setPolicyPermissions] = useState({
    can_manage: false,
    can_create: false,
    can_edit: false,
    can_delete: false
  });
  
  // Can manage policies (Super Admin OR delegated access)
  const canManagePolicies = isSuperAdmin || policyPermissions.can_manage || policyPermissions.can_create;
  
  const [leaveForm, setLeaveForm] = useState({
    staff_id: '',
    leave_type: 'annual',
    start_date: '',
    end_date: '',
    reason: ''
  });

  const [trainingForm, setTrainingForm] = useState({
    staff_id: '',
    training_name: '',
    provider: '',
    completion_date: '',
    expiry_date: '',
    certificate_number: '',
    status: 'completed'
  });

  const [policyForm, setPolicyForm] = useState({
    title: '',
    category: 'employment',
    content: '',
    version: '1.0',
    effective_date: ''
  });

  useEffect(() => {
    fetchData();
    fetchPolicyPermissions();
  }, []);

  const fetchPolicyPermissions = async () => {
    try {
      const res = await api.get('/hr/policies/check-permissions');
      setPolicyPermissions(res.data);
    } catch (error) {
      console.log('Could not fetch policy permissions');
    }
  };

  const fetchData = async () => {
    try {
      const [staffRes, leaveRes, trainingRes, policiesRes] = await Promise.all([
        api.get('/staff'),
        api.get('/hr/leave-requests').catch(() => ({ data: [] })),
        api.get('/hr/training').catch(() => ({ data: [] })),
        api.get('/hr/policies').catch(() => ({ data: [] }))
      ]);
      setStaff(staffRes.data);
      setLeaveRequests(leaveRes.data);
      setTraining(trainingRes.data);
      setPolicies(policiesRes.data);
    } catch (error) {
      console.error('Failed to fetch HR data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/hr/leave-requests', leaveForm);
      toast.success('Leave request submitted');
      setLeaveDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit leave request');
    }
  };

  const handleTrainingSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/hr/training', trainingForm);
      toast.success('Training record added');
      setTrainingDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add training record');
    }
  };

  const approveLeave = async (leaveId) => {
    try {
      await api.put(`/hr/leave-requests/${leaveId}/approve`);
      toast.success('Leave approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve leave');
    }
  };

  const handlePrintLeave = (leave) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Leave Request - ${leave.staff_name}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; }
            .header { text-align: center; border-bottom: 2px solid #1e3a5f; padding-bottom: 20px; margin-bottom: 20px; }
            .logo { font-size: 24px; font-weight: bold; color: #1e3a5f; }
            .info { background: #f8fafc; padding: 20px; border-radius: 8px; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="logo">ICRCL</div>
            <p>${ICRCL_NAME}</p>
          </div>
          <h2>LEAVE REQUEST</h2>
          <div class="info">
            <p><strong>Staff Member:</strong> ${leave.staff_name}</p>
            <p><strong>Leave Type:</strong> ${leave.leave_type?.replace('_', ' ')}</p>
            <p><strong>Start Date:</strong> ${leave.start_date}</p>
            <p><strong>End Date:</strong> ${leave.end_date}</p>
            <p><strong>Status:</strong> ${leave.status}</p>
            <p><strong>Reason:</strong> ${leave.reason || 'N/A'}</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleEmailLeave = (leave) => {
    toast.success(`Email leave request - From: ${ICRCL_EMAIL}`);
  };

  const handlePrintDocument = (docName) => {
    toast.success(`Printing ${docName}`);
  };

  const handleDownloadDocument = (docName) => {
    toast.success(`Downloading ${docName}`);
  };

  const handleEmailDocument = (docName) => {
    toast.success(`Email ${docName} - From: ${ICRCL_EMAIL}`);
  };

  const handlePolicySubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/hr/policies', policyForm);
      toast.success('Policy created successfully');
      setPolicyDialogOpen(false);
      setPolicyForm({ title: '', category: 'employment', content: '', version: '1.0', effective_date: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create policy');
    }
  };

  const handleDeletePolicy = async (policyId) => {
    if (!window.confirm('Are you sure you want to delete this policy?')) return;
    try {
      await api.delete(`/hr/policies/${policyId}`);
      toast.success('Policy deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete policy');
    }
  };

  const defaultPolicies = [
    'Employment Contract Template',
    'Staff Handbook', 
    'Disciplinary Policy',
    'Grievance Policy',
    'Supervision Record Template',
    'Annual Leave Policy',
    'Sickness Absence Policy'
  ];

  const mandatoryTrainingList = [
    { name: 'Safeguarding Children Level 3', required: true, renewalPeriod: '3 years' },
    { name: 'Basic Life Support (BLS)', required: true, renewalPeriod: '1 year' },
    { name: 'Manual Handling', required: true, renewalPeriod: '2 years' },
    { name: 'Infection Control', required: true, renewalPeriod: '2 years' },
    { name: 'Fire Safety', required: true, renewalPeriod: '1 year' },
    { name: 'GDPR/Data Protection', required: true, renewalPeriod: '2 years' },
    { name: 'Health & Safety', required: true, renewalPeriod: '2 years' },
    { name: 'Equality & Diversity', required: false, renewalPeriod: '3 years' }
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Document Action Requests Panel - for HR/Admin/Directors */}
      <DocumentActionRequestsPanel />
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1e3a5f]">HR & Workforce</h1>
          <p className="text-slate-500 mt-1">Manage staff, leave, training and compliance</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={leaveDialogOpen} onOpenChange={setLeaveDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-full" data-testid="new-leave-btn">
                <CalendarIcon className="w-4 h-4 mr-2" />
                Request Leave
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Request Leave</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleLeaveSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Staff Member</Label>
                  <Select value={leaveForm.staff_id} onValueChange={(v) => setLeaveForm({...leaveForm, staff_id: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select staff..." />
                    </SelectTrigger>
                    <SelectContent>
                      {staff.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Leave Type</Label>
                  <Select value={leaveForm.leave_type} onValueChange={(v) => setLeaveForm({...leaveForm, leave_type: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="annual">Annual Leave</SelectItem>
                      <SelectItem value="sick">Sick Leave</SelectItem>
                      <SelectItem value="compassionate">Compassionate Leave</SelectItem>
                      <SelectItem value="maternity">Maternity Leave</SelectItem>
                      <SelectItem value="paternity">Paternity Leave</SelectItem>
                      <SelectItem value="unpaid">Unpaid Leave</SelectItem>
                      <SelectItem value="study">Study Leave</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input type="date" value={leaveForm.start_date} onChange={(e) => setLeaveForm({...leaveForm, start_date: e.target.value})} required />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input type="date" value={leaveForm.end_date} onChange={(e) => setLeaveForm({...leaveForm, end_date: e.target.value})} required />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Reason</Label>
                  <Textarea value={leaveForm.reason} onChange={(e) => setLeaveForm({...leaveForm, reason: e.target.value})} />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setLeaveDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-[#1e3a5f]">Submit Request</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={trainingDialogOpen} onOpenChange={setTrainingDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90 rounded-full" data-testid="add-training-btn">
                <GraduationCap className="w-4 h-4 mr-2" />
                Add Training
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Training Record</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleTrainingSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Staff Member</Label>
                  <Select value={trainingForm.staff_id} onValueChange={(v) => setTrainingForm({...trainingForm, staff_id: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select staff..." />
                    </SelectTrigger>
                    <SelectContent>
                      {staff.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Training Name</Label>
                  <Select value={trainingForm.training_name} onValueChange={(v) => setTrainingForm({...trainingForm, training_name: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select training..." />
                    </SelectTrigger>
                    <SelectContent>
                      {mandatoryTrainingList.map(t => (
                        <SelectItem key={t.name} value={t.name}>{t.name}</SelectItem>
                      ))}
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Provider</Label>
                  <Input value={trainingForm.provider} onChange={(e) => setTrainingForm({...trainingForm, provider: e.target.value})} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Completion Date</Label>
                    <Input type="date" value={trainingForm.completion_date} onChange={(e) => setTrainingForm({...trainingForm, completion_date: e.target.value})} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Expiry Date</Label>
                    <Input type="date" value={trainingForm.expiry_date} onChange={(e) => setTrainingForm({...trainingForm, expiry_date: e.target.value})} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Certificate Number</Label>
                  <Input value={trainingForm.certificate_number} onChange={(e) => setTrainingForm({...trainingForm, certificate_number: e.target.value})} />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setTrainingDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-[#1e3a5f]">Add Record</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="card-icrcl">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#1e3a5f]/10 flex items-center justify-center">
                <User className="w-5 h-5 text-[#1e3a5f]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1e3a5f]">{staff.length}</p>
                <p className="text-sm text-slate-500">Total Staff</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-icrcl">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600">{leaveRequests.filter(l => l.status === 'pending').length}</p>
                <p className="text-sm text-slate-500">Pending Leave</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-icrcl">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{training.length}</p>
                <p className="text-sm text-slate-500">Training Records</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-icrcl">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">0</p>
                <p className="text-sm text-slate-500">Expiring Soon</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="leave" className="space-y-6">
        <TabsList>
          <TabsTrigger value="leave">
            <CalendarIcon className="w-4 h-4 mr-2" />
            Leave Management
          </TabsTrigger>
          <TabsTrigger value="training">
            <GraduationCap className="w-4 h-4 mr-2" />
            Training & CPD
          </TabsTrigger>
          <TabsTrigger value="compliance">
            <Shield className="w-4 h-4 mr-2" />
            Compliance
          </TabsTrigger>
          <TabsTrigger value="documents">
            <FileText className="w-4 h-4 mr-2" />
            HR Documents
          </TabsTrigger>
        </TabsList>

        <TabsContent value="leave">
          <Card className="card-icrcl">
            <CardHeader>
              <CardTitle className="text-lg">Leave Requests</CardTitle>
              <CardDescription>Manage staff leave and holidays</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {leaveRequests.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <CalendarIcon className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No leave requests</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="table-icrcl">
                      <TableHead>Staff</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Dates</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leaveRequests.map((leave) => (
                      <TableRow key={leave.id}>
                        <TableCell>{leave.staff_name}</TableCell>
                        <TableCell className="capitalize">{leave.leave_type?.replace('_', ' ')}</TableCell>
                        <TableCell>{leave.start_date} - {leave.end_date}</TableCell>
                        <TableCell>
                          <Badge className={
                            leave.status === 'approved' ? 'badge-completed' :
                            leave.status === 'pending' ? 'badge-pending' :
                            'badge-cancelled'
                          }>
                            {leave.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {leave.status === 'pending' && (
                              <Button size="sm" onClick={() => approveLeave(leave.id)}>Approve</Button>
                            )}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handlePrintLeave(leave)}>
                                  <Printer className="w-4 h-4 mr-2" />
                                  Print
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleEmailLeave(leave)}>
                                  <Mail className="w-4 h-4 mr-2" />
                                  Send Email
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="training">
          <Card className="card-icrcl">
            <CardHeader>
              <CardTitle className="text-lg">Training Records</CardTitle>
              <CardDescription>Staff training and CPD tracking</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <h4 className="font-medium text-slate-700">Mandatory Training Requirements</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {mandatoryTrainingList.map((t, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        {t.required && <span className="w-2 h-2 rounded-full bg-red-500"></span>}
                        <span className="font-medium">{t.name}</span>
                      </div>
                      <Badge variant="outline">{t.renewalPeriod}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="compliance">
          <Card className="card-icrcl">
            <CardHeader>
              <CardTitle className="text-lg">Compliance Tracking</CardTitle>
              <CardDescription>DBS checks, right to work, and regulatory compliance</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-green-700">DBS Checks</span>
                    </div>
                    <p className="text-2xl font-bold text-green-800">{staff.length}</p>
                    <p className="text-sm text-green-600">All staff cleared</p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Building className="w-5 h-5 text-blue-600" />
                      <span className="font-medium text-blue-700">Right to Work</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-800">{staff.length}</p>
                    <p className="text-sm text-blue-600">Verified</p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="w-5 h-5 text-purple-600" />
                      <span className="font-medium text-purple-700">Professional Registration</span>
                    </div>
                    <p className="text-2xl font-bold text-purple-800">{staff.filter(s => s.role === 'therapist').length}</p>
                    <p className="text-sm text-purple-600">HCPC/RCSLT registered</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documents">
          <Card className="card-icrcl">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">HR Documents & Policies</CardTitle>
                <CardDescription>Contracts, policies, and staff documents</CardDescription>
              </div>
              {canManagePolicies && (
                <Dialog open={policyDialogOpen} onOpenChange={setPolicyDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90 rounded-full" data-testid="add-policy-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Policy
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Create New Policy</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handlePolicySubmit} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Policy Title *</Label>
                          <Input 
                            value={policyForm.title} 
                            onChange={(e) => setPolicyForm({...policyForm, title: e.target.value})} 
                            placeholder="e.g., Data Protection Policy"
                            required 
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Category *</Label>
                          <Select value={policyForm.category} onValueChange={(v) => setPolicyForm({...policyForm, category: v})}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="employment">Employment</SelectItem>
                              <SelectItem value="leave">Leave & Absence</SelectItem>
                              <SelectItem value="disciplinary">Disciplinary</SelectItem>
                              <SelectItem value="health_safety">Health & Safety</SelectItem>
                              <SelectItem value="safeguarding">Safeguarding</SelectItem>
                              <SelectItem value="clinical">Clinical Practice</SelectItem>
                              <SelectItem value="data_protection">Data Protection</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Version</Label>
                          <Input 
                            value={policyForm.version} 
                            onChange={(e) => setPolicyForm({...policyForm, version: e.target.value})} 
                            placeholder="1.0"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Effective Date *</Label>
                          <Input 
                            type="date" 
                            value={policyForm.effective_date} 
                            onChange={(e) => setPolicyForm({...policyForm, effective_date: e.target.value})} 
                            required 
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Policy Content *</Label>
                        <Textarea 
                          value={policyForm.content} 
                          onChange={(e) => setPolicyForm({...policyForm, content: e.target.value})} 
                          rows={8}
                          placeholder="Enter the full policy text..."
                          required
                        />
                      </div>
                      <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setPolicyDialogOpen(false)}>Cancel</Button>
                      <Button type="submit" className="bg-[#1e3a5f]">Create Policy</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
              )}
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Custom Policies */}
                {policies.length > 0 && (
                  <div>
                    <h4 className="font-medium text-slate-700 mb-3">Custom Policies</h4>
                    <div className="space-y-3">
                      {policies.map((policy) => (
                        <div key={policy.id} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg hover:bg-blue-100 border border-blue-200">
                          <div className="flex items-center gap-3 cursor-pointer" onClick={() => setViewPolicyDialog(policy)}>
                            <FileText className="w-5 h-5 text-blue-600" />
                            <div>
                              <span className="font-medium">{policy.title}</span>
                              <p className="text-xs text-slate-500">v{policy.version} | {policy.category?.replace('_', ' ')} | Effective: {policy.effective_date}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            {/* View button - available to everyone */}
                            <Button variant="ghost" size="icon" onClick={() => setViewPolicyDialog(policy)} title="View Policy">
                              <Eye className="w-4 h-4 text-slate-500" />
                            </Button>
                            {/* Management actions - Super Admin only */}
                            {canManagePolicies && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon">
                                    <MoreHorizontal className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handlePrintDocument(policy.title)}>
                                    <Printer className="w-4 h-4 mr-2" />
                                    Print
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleDownloadDocument(policy.title)}>
                                    <Download className="w-4 h-4 mr-2" />
                                    Download
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleEmailDocument(policy.title)}>
                                    <Mail className="w-4 h-4 mr-2" />
                                    Send via Email
                                  </DropdownMenuItem>
                                  {(isSuperAdmin || policyPermissions.can_edit) && (
                                    <DropdownMenuItem onClick={() => setEditPolicyDialog(policy)}>
                                      <FileText className="w-4 h-4 mr-2" />
                                      Edit
                                    </DropdownMenuItem>
                                  )}
                                  {(isSuperAdmin || policyPermissions.can_delete) && (
                                    <DropdownMenuItem onClick={() => handleDeletePolicy(policy.id)} className="text-red-600">
                                      <AlertTriangle className="w-4 h-4 mr-2" />
                                      Delete
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Default Templates */}
                <div>
                  <h4 className="font-medium text-slate-700 mb-3">Standard Templates</h4>
                  <div className="space-y-3">
                    {defaultPolicies.map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-[#1e3a5f]" />
                          <span>{doc}</span>
                        </div>
                        {canManagePolicies && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handlePrintDocument(doc)}>
                                <Printer className="w-4 h-4 mr-2" />
                                Print
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDownloadDocument(doc)}>
                                <Download className="w-4 h-4 mr-2" />
                                Download
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleEmailDocument(doc)}>
                                <Mail className="w-4 h-4 mr-2" />
                                Send via Email
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* View Policy Dialog */}
      <Dialog open={!!viewPolicyDialog} onOpenChange={() => setViewPolicyDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{viewPolicyDialog?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-4 text-sm text-slate-500">
              <Badge variant="outline">v{viewPolicyDialog?.version}</Badge>
              <span className="capitalize">{viewPolicyDialog?.category?.replace('_', ' ')}</span>
              {viewPolicyDialog?.effective_date && (
                <span>Effective: {new Date(viewPolicyDialog.effective_date).toLocaleDateString('en-GB')}</span>
              )}
            </div>
            <div className="prose prose-slate max-w-none">
              <div className="whitespace-pre-wrap bg-slate-50 p-4 rounded-lg">
                {viewPolicyDialog?.content || 'No content available.'}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewPolicyDialog(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
